import type { PropsWithChildren, ReactNode } from 'react';
import { useContext } from 'react';
import { createStyles } from '@mantine/core';

import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { FEATURE } from '~/common/enums/feature.enum';
import { useIsTablet } from '~/domains/common/hooks/mediaQueries';
import { ContestChatModalContext } from '../ContestChat/ContestChatModals/ContestChatModalContext';
import {
  CHAT_PANEL_WIDTH,
  CHAT_PANEL_GAP,
  CHAT_PANEL_RIGHT_PADDING,
  CHAT_PANEL_TOP_PADDING,
  CHAT_PANEL_BOTTOM_PADDING,
  CHAT_PANEL_MIN_HEIGHT,
} from '../ContestChat/constants/chatPanel';

const DEFAULT_HEADER_HEIGHT = 120;

export type ChatLayoutMode = 'centered' | 'anchored-left';

type StyleParams = {
  isPanelOpen: boolean;
  layoutMode: ChatLayoutMode;
  headerHeight: number;
};

const useStyles = createStyles((theme, { isPanelOpen, layoutMode, headerHeight }: StyleParams) => {
  const isAnchoredLeft = layoutMode === 'anchored-left';
  // Panel height: viewport - header - top padding - bottom padding
  const panelHeight = `calc(100vh - ${headerHeight}px - ${CHAT_PANEL_TOP_PADDING}px - ${CHAT_PANEL_BOTTOM_PADDING}px)`;

  const getMaxWidth = () => {
    if (isAnchoredLeft) return '100%';
    if (isPanelOpen) return 'calc(100% - 120px)';
    return '100%';
  };

  return {
    layoutContainer: {
      display: 'flex',
      justifyContent: isAnchoredLeft ? 'flex-start' : 'center',
      width: '100%',
      maxWidth: getMaxWidth(),
      margin: isAnchoredLeft ? 0 : '0 auto',
      paddingRight: isAnchoredLeft && isPanelOpen ? CHAT_PANEL_RIGHT_PADDING : 0,
      height: 'fit-content',
      transition: isAnchoredLeft ? 'padding-right 0.3s ease-in-out' : 'max-width 0.3s ease-in-out',
    },
    mainContentWrapper: {
      marginRight: isPanelOpen ? CHAT_PANEL_WIDTH + CHAT_PANEL_GAP : 0,
      transition: 'margin-right 0.3s ease-in-out',
      width: '100%',
      minHeight: 'inherit',
      minWidth: 0,
      overflow: 'visible',
    },
    chatPanelWrapper: {
      position: 'fixed',
      right: CHAT_PANEL_RIGHT_PADDING,
      top: headerHeight + CHAT_PANEL_TOP_PADDING,
      width: CHAT_PANEL_WIDTH,
      height: panelHeight,
      minHeight: CHAT_PANEL_MIN_HEIGHT,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      transform: isPanelOpen
        ? 'translateX(0)'
        : `translateX(${CHAT_PANEL_WIDTH + CHAT_PANEL_RIGHT_PADDING}px)`,
      transition: 'transform 0.3s ease-in-out',
      zIndex: 100,
    },
    chatPanelInner: {
      width: '100%',
      height: '100%',
      position: 'relative',
      isolation: 'isolate',
      display: 'flex',
      flexDirection: 'column',
    },
  };
});

type GamecenterLayoutWithChatProps = PropsWithChildren<{
  chatSlot?: ReactNode;
  layoutMode?: ChatLayoutMode;
  headerHeight?: number;
}>;

function GamecenterLayoutWithChat({
  children,
  chatSlot,
  layoutMode = 'centered',
  headerHeight = DEFAULT_HEADER_HEIGHT,
}: GamecenterLayoutWithChatProps) {
  const isChatWebEnabled = useIsFeatureEnabled(FEATURE.ENABLE_CHAT_WEB);
  const isMobile = useIsTablet();
  const { isChatPanelOpen, isChatPanelClosing } = useContext(ContestChatModalContext);

  const isPanelOpen = isChatPanelOpen && !isChatPanelClosing;
  const { classes } = useStyles({ isPanelOpen, layoutMode, headerHeight });

  if (!isChatWebEnabled || isMobile) {
    return children as ReactNode;
  }

  const shouldShowContent = isChatPanelOpen || isChatPanelClosing;

  return (
    <div className={classes.layoutContainer}>
      <div className={classes.mainContentWrapper}>{children}</div>

      <div className={classes.chatPanelWrapper}>
        <div className={classes.chatPanelInner}>{shouldShowContent ? chatSlot : null}</div>
      </div>
    </div>
  );
}

export default GamecenterLayoutWithChat;
